//-------------------------------------------------------------------------------------------------- Imports
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    GenericResponse, ResponseMessages, ResponseSuscriptorData,
    ResponseSuscriptorDataGeneralDatum, ResponseSuscriptorDataHiredService
} from "../../types/common";
import { getSuscriptorData, getSuscriptorNotifications } from "../../methods/Methods";
import { AxiosError } from "axios";
import _ from "lodash";



//-------------------------------------------------------------------------------------------------- Interfaces
export interface FillThunkSliceInterface {
    clientId: string;
};

export interface ContractInterface extends ResponseSuscriptorDataGeneralDatum {
    notifications?: Array<ResponseMessages> | null;
};

export interface GeneralDataSliceInterface {
    contracts: Array<ResponseSuscriptorDataGeneralDatum>;
    selectedContract: ContractInterface | null;
    status: number;
};

export interface HiredServicesInterface {
    clientId: string;
    hiredServices: Array<ResponseSuscriptorDataHiredService>;
};

export interface AliasInterface {
    idCliente: string;
    alias: string;
    action: "UPDATE" | "ADD" | "DELETE"
};

export interface SetNotificationsInterface {
    clientId: string;
    notifications: Array<ResponseMessages>;
};



//-------------------------------------------------------------------------------------------------- Vars and consts
let statusOfRequest = 0;
const generalDataSliceInitialState = {
    contracts: [],
    selectedContract: null,
    status: 0
} as GeneralDataSliceInterface;



//-------------------------------------------------------------------------------------------------- Thunks
const fillGeneralDataSlice = createAsyncThunk(
    "thunk/generalData",
    async (object: FillThunkSliceInterface): Promise<Array<ResponseSuscriptorDataGeneralDatum>> => {
        let data = [] as ResponseSuscriptorDataGeneralDatum[];

        await getSuscriptorData(object.clientId)
            .then((response: GenericResponse<ResponseSuscriptorData>) => {
                if (!_.isEmpty(response.data[0].generalData[0].paquete) && response.data[0].generalData[0].registrado === 1) {
                    statusOfRequest = 1;
                    data = response.data[0].generalData;
                } else {
                    statusOfRequest = 2;
                }
            })
            .catch((error: AxiosError<any>) => {
                statusOfRequest = 2;
            });

        return data;
    }
);

const fillNotificationsSlice = createAsyncThunk(
    "thunk/notifications",
    async (object: FillThunkSliceInterface): Promise<Array<ResponseMessages>> => {
        let data = [] as ResponseMessages[];

        await getSuscriptorNotifications(object.clientId)
            .then((response: GenericResponse<ResponseMessages>) => {
                if (response.data) {
                    data = response.data;
                }
            });

        return data;
    }
);



//-------------------------------------------------------------------------------------------------- Slice
const generalDataSlice = createSlice({
    name: "generalData",
    initialState: generalDataSliceInitialState,
    reducers: {
        setGeneralData: (state, action: PayloadAction<GeneralDataSliceInterface>) => {
            state.contracts = action.payload.contracts;
            state.selectedContract = action.payload.selectedContract;
        },
        setSelectedContract: (state, action: PayloadAction<ResponseSuscriptorDataGeneralDatum>) => {
            state.selectedContract = action.payload;
        },
        setHiredServices: (state, action: PayloadAction<HiredServicesInterface>) => {
            state.contracts[
                state.contracts.findIndex(
                    obj => obj.sucursal + obj.suscriptor === action.payload.clientId
                )
            ].hiredServices = action.payload.hiredServices;

            if (
                state.selectedContract &&
                state.selectedContract.sucursal +
                state.selectedContract.suscriptor === action.payload.clientId
            ) {
                state.selectedContract.hiredServices = action.payload.hiredServices;
            }
        },
        setUpdateAlias: (state, action: PayloadAction<AliasInterface>) => {
            switch (action.payload.action) {
                case "DELETE":
                    let index = state.contracts.findIndex(
                        obj => obj.sucursal + obj.suscriptor === action.payload.idCliente
                    );
                    if (index > -1) {
                        state.contracts.splice(index, 1);
                    }
                    break;
                case "UPDATE":
                    state.contracts[
                        state.contracts.findIndex(
                            obj => obj.sucursal + obj.suscriptor === action.payload.idCliente
                        )
                    ].alias = action.payload.alias;

                    if (
                        state.selectedContract &&
                        state.selectedContract.sucursal +
                        state.selectedContract.suscriptor === action.payload.idCliente
                    ) {
                        state.selectedContract.alias = action.payload.alias;
                    }
                    break;
                default:
                    break;
            }


        },
        setReadNotification: (state, action: PayloadAction<number>) => {
            if (state.selectedContract && state.selectedContract.notifications) {
                state.selectedContract.notifications[
                    state.selectedContract.notifications.findIndex(
                        obj => obj.id_message === action.payload
                    )
                ].read = true;
            }
        },
        clearGeneralData: (state) => {
            statusOfRequest = 0;
            state.contracts = generalDataSliceInitialState.contracts;
            state.selectedContract = generalDataSliceInitialState.selectedContract;
            state.status = generalDataSliceInitialState.status;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fillGeneralDataSlice.fulfilled, (state, action) => {
            state.contracts = action.payload;
            state.status = statusOfRequest;
        });
        builder.addCase(fillNotificationsSlice.fulfilled, (state, action) => {
            if (state.selectedContract) {
                state.selectedContract.notifications = action.payload;
            }
        });
    }
});



//-------------------------------------------------------------------------------------------------- Exports
export { fillGeneralDataSlice, fillNotificationsSlice };
export const {
    setGeneralData, setSelectedContract, setHiredServices, setReadNotification, clearGeneralData, setUpdateAlias
} = generalDataSlice.actions;
export const getGeneralData = (state: any): GeneralDataSliceInterface => state.generalData;
export default generalDataSlice.reducer;
